import React, { useContext } from "react";
import { observer } from "mobx-react";
import EventViewStore from "@state/stores/offer/event/EventViewStore";
import { AdditionalOfferViewStore } from "@state/stores/offer/shared/AdditionalOfferViewStore";
import { StatisticsGroups } from "@data-types/offer/event/EventViewStoreEnums";
import {
	CenteredSwiper,
	useCenteredSwiperSlide,
	UseCenteredSwiperSlideArgs,
} from "@components-shared/desktop/centered-swiper";
import { SwiperSlide } from "swiper/react";
import { FullStatisticsButton } from "@features/offer/shared/event/FullStatisticsButton";
import { LiveStatisticsButton } from "@features/offer/shared/event/LiveStatisticsButton";
import { StreaksStatisticsButton } from "@features/offer/shared/event/StreaksStatisticsButton";
import { OfferStoreContext } from "@gp/components";
import { EventInsightsButton } from "@features/offer/shared/event/EventInsightsButton";
import { PlayerStatisticsButton } from "@features/offer/shared/event/PlayerStatisticsButton";
import { TeamStandingsButton } from "@features/offer/shared/event/TeamStandingsButton";
import { TeamLineupsButton } from "@features/offer/shared/event/TeamLineupsButton";

export type HeaderPills = {
	type: StatisticsGroups;
	Component: typeof LiveStatisticsButton;
	props?: any;
};

export default observer(function EventStatisticsHeaderPills({
	store,
}: {
	store: EventViewStore | AdditionalOfferViewStore;
}) {
	const {
		sportStatisticsStore,
		playerStatisticsStore, 
		teamLineupsStore,
		teamStandingsStatsStore,
	} = store;

	const statisticsPills = (function () {
		const pills: HeaderPills[] = [];

        if (sportStatisticsStore.isMatchStatsAvailable) {
			pills.push({
				type: StatisticsGroups.LiveStats,
				Component: LiveStatisticsButton,
			});
		}

		if (sportStatisticsStore.isStreakStatAvailable) {
			pills.push({
				type: StatisticsGroups.StreakStats,
				Component: StreaksStatisticsButton,
			});
		}

        if (teamLineupsStore.isTeamLineupsAvailable) {
			pills.push({
				type: StatisticsGroups.TeamLineups,
				Component: TeamLineupsButton,
			});
		}

		if (playerStatisticsStore.isPlayerStatsAvailable) {
			pills.push({
				type: StatisticsGroups.PlayerStats,
				Component: PlayerStatisticsButton,
			});
		}

		if (sportStatisticsStore.isEventInsightsAvailable) {
			pills.push({
				type: StatisticsGroups.EventInsights,
				Component: EventInsightsButton,
			});
		}

        if (teamStandingsStatsStore.isTeamStandingsStatsAvailable) {
			pills.push({
				type: StatisticsGroups.TeamStandings,
				Component: TeamStandingsButton,
			});
		}

		if (sportStatisticsStore.isFullStatsAvailable) {
			pills.push({
				type: StatisticsGroups.FullStats,
				Component: FullStatisticsButton,
			});
		}

		return pills;
	})();

	return (
		<CenteredSwiper customClass="tabs--swiper__nav">
			{statisticsPills.map((pill, index) => (
				<SwiperSlide key={pill.type} style={{ width: "auto" }}>
					<EventStatisticsHeaderPillsButton
						index={index}
						isActive={store.displayStatisticsGroup === pill.type}
						headerPills={pill}
					/>
				</SwiperSlide>
			))}
		</CenteredSwiper>
	);
});

export function EventStatisticsHeaderPillsButton({
	headerPills,
	isActive,
	index,
}: {
	headerPills: HeaderPills;
} & UseCenteredSwiperSlideArgs) {
	const store = useContext(OfferStoreContext) as
		| EventViewStore
		| AdditionalOfferViewStore;
	useCenteredSwiperSlide({ isActive, index });

	return <headerPills.Component store={store} />;
}
